// Manufacturer/Supplier picker — SearchableSelect has no built-in "create new" row, so this
// is a small bespoke combobox (mirrors the ProductSearch pattern used elsewhere in this app)
// that lets an admin either pick an existing directory entry or type a new name and create it
// inline. See docs/SUPPLIERS_MODULE.md. Shared by IngredientsHub (manufacturer/supplier fields)
// and the Ingredient Receiving Log form.
import React, { useState } from 'react';
import supplierService from '../services/supplierService';

export default function SupplierPickerField({ options, value, onChange, onCreated, placeholder }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  // Resolve the current value against the full list (so an already-chosen archived supplier still
  // displays its name correctly), but only offer active suppliers as new picks — archived ones are
  // meant to drop out of pickers, see docs/SUPPLIERS_MODULE.md.
  const selected = options.find(o => String(o.id) === String(value));
  const selectableOptions = options.filter(o => o.isActive !== false);
  const filtered = query.trim()
    ? selectableOptions.filter(o => o.name.toLowerCase().includes(query.trim().toLowerCase()))
    : selectableOptions;
  // Checked against the full list (not just active) so typing an archived supplier's exact name
  // doesn't silently offer to create a duplicate — reactivating it is a Suppliers Hub action.
  const exactMatch = options.some(o => o.name.toLowerCase() === query.trim().toLowerCase());

  const handleCreate = async () => {
    const name = query.trim();
    if (!name || creating) return;
    setCreating(true);
    try {
      const created = await supplierService.createSupplier({ name });
      onCreated();
      onChange(String(created.id));
      setQuery('');
      setOpen(false);
    } catch {
      // leave the dropdown open so the admin can retry
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="position-relative">
      <input
        className="form-control form-control-sm"
        placeholder={placeholder}
        value={open ? query : (selected ? selected.name : '')}
        onFocus={() => { setOpen(true); setQuery(''); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={e => setQuery(e.target.value)}
      />
      {open && (
        <ul className="list-group position-absolute w-100 shadow-sm" style={{ zIndex: 1050, maxHeight: 220, overflowY: 'auto' }}>
          {value && (
            <li className="list-group-item list-group-item-action py-1 small text-muted"
              onMouseDown={() => { onChange(''); setOpen(false); }}>
              — Clear —
            </li>
          )}
          {filtered.map(o => (
            <li key={o.id} className="list-group-item list-group-item-action py-1 small"
              onMouseDown={() => { onChange(String(o.id)); setOpen(false); }}>
              {o.name}
            </li>
          ))}
          {query.trim() && !exactMatch && (
            <li className="list-group-item list-group-item-action py-1 small text-primary"
              onMouseDown={handleCreate}>
              {creating ? 'Creating…' : `+ Add "${query.trim()}" as new supplier`}
            </li>
          )}
          {query.trim() && exactMatch && filtered.length === 0 && (
            <li className="list-group-item py-1 small text-muted">
              "{query.trim()}" exists but is archived — reactivate it from the directory to use it again.
            </li>
          )}
          {filtered.length === 0 && !query.trim() && (
            <li className="list-group-item py-1 small text-muted">No suppliers yet — type to create one.</li>
          )}
        </ul>
      )}
    </div>
  );
}
